<template>
  <div class="meeting-booking-container">
    <div :class="{ 'container-fluid': currentStep === 'payment', 'container': currentStep !== 'payment' }">
      <div class="text-center mt-4"
        v-if="isLoading">
        <HMSpinner />
      </div>
      <div v-else-if="meetingType">
        <div v-show="currentStep === 'calendar'"
          class="row mt-5">
          <div class="col-12 text-center">
            <HMInstructorsStack :instructors="meetingType.instructors"
              :size="'65px'"
              :clickable="false" />
          </div>
          <div class="col-12 offset-md-3 col-md-6 mt-4 text-center">
            <h2>
              {{ meetingType.name }} {{ $t('MARKETING.LABELS.WITH') }}
              {{ instructorNames }}
            </h2>
            <HMVueEditorPreview v-if="meetingType.description"
              :html="meetingType.description"
              class="mt-3" />
          </div>
          <div class="col-12 mt-2">
            <hr />
          </div>
          <div class="col-12 mt-4"
            v-if="currentStep === 'calendar'">
            <PublicSchedulingCalendar class="transition-card"
              :studioURL="studioURL"
              :meetingType="meetingType"
              @selectedSlot="selectSlot" />
          </div>
        </div>
        <div v-show="currentStep === 'questions'"
          class="row mt-5">
          <div class="col-12 d-flex flex-row flex-nowrap justify-content-between align-items-center">
            <h1>{{ meetingType.name }}</h1>
            <div class="d-flex flex-row justify-content-end align-items-center">
              <HMInstructorsStack :instructors="meetingType.instructors"
                :size="'30px'"
                :clickable="false" />
              <span class="c-charcoal ms-3">{{ instructorNames }}</span>
            </div>
          </div>
          <div class="col-12 mt-2">
            <hr />
          </div>
          <div class="col-12 mt-4 offset-md-2 col-md-8">
            <div class="mb-3 h4">
              {{
                $t(
                  'MARKETING.TEXT.PLEASE_ANSWER_THESE_QUESTIONS_TO_COMPLETE_YOUR_BOOKING'
                )
              }}
            </div>
            <Form @submit="book"
              tag="form"
              ref="form"
              v-slot="{ meta }">
              <label>{{ $t('MARKETING.LABELS.FULL_NAME') }}</label>
              <HMTextInput type="text"
                rules="required|max:128"
                name="Full name"
                v-model="bookingFullName" />
              <label>{{ $t('MARKETING.LABELS.EMAIL_ADDRESS') }}</label>
              <HMTextInput type="text"
                rules="required|email|max:128"
                name="email address"
                v-model="bookingEmail" />
              <div class="form-group"
                v-for="question in questions"
                :key="question.position">
                <label>{{ question.label }}</label>
                <HMTextInput type="text"
                  :rules="question.required ? 'required' : ''"
                  :name="`answer-${question.position}`"
                  v-model="question.answer" />
              </div>
              <div class="d-flex flex-row justify-content-between align-items-center mt-4">
                <div @click="currentStep = 'calendar'"
                  class="pointer">
                  <i class="fa fa-chevron-left me-2"></i>
                  {{ $t('MARKETING.LABELS.BACK_TO_CALENDAR') }}
                </div>
                <HMButton color="brand"
                  :disabled="!meta.valid || isBooking"
                  type="submit"
                  :loading="isBooking">
                  {{ shouldShowPayment ? $t('MARKETING.BUTTONS.CONTINUE_TO_PAYMENT') :
                    $t('MARKETING.BUTTONS.COMPLETE_BOOKING') }}
                </HMButton>
              </div>
            </Form>
          </div>
        </div>
        <div v-show="currentStep === 'payment'"
          class="row">
          <div class="offset-md-2 offset-lg-2 col-12 col-md-8 col-lg-5 mt-2 mt-lg-4 p-lg-0">
            <div class="row">
              <div class="col-12">
                <div class="d-flex">
                  <span class="back-button pointer d-md-none"
                    @click="$router.go(-1)">
                    <i class="fal fa-chevron-left"></i>
                  </span>
                  <StudioLogo class="logo-image mb-2"
                    :studio="studio" />
                </div>
              </div>
              <div class="col-12 mt-2">
                <div class="d-flex flex-row justify-content-start align-items-center mt-4 small">
                  <span @click="currentStep = 'calendar'"
                    class="pointer">
                    <span class="c-brand-color">{{ $t('MARKETING.LABELS.CALENDAR') }}</span>
                    <i class="fal fa-chevron-right mx-2"></i>
                  </span>
                  <span @click="currentStep = 'questions'"
                    class="pointer">
                    <span class="c-brand-color">{{ $t('MARKETING.LABELS.QUESTIONS') }}</span>
                    <i class="fal fa-chevron-right mx-2"></i>
                  </span>
                  <span>
                    {{ $t('MARKETING.LABELS.CHECKOUT') }}
                  </span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-12 d-block d-lg-none">
                <MeetingCheckoutSummary v-if="meetingType"
                  key="mobile-summary"
                  class="mt-3 mobile"
                  :meetingType="meetingType"
                  :currency="currency"
                  :instructorNames="instructorNames"
                  :selectedSlot="selectedSlot"
                  :timezone="timezone" />
                <hr />
              </div>
              <div class="col-12 mt-2 mt-lg-4">
                <h2>{{ $t('MARKETING.LABELS.CHECKOUT') }}</h2>
              </div>
              <div v-if="isLoggedIn && user"
                class="col-12 currently-logged-in mt-2 mb-4">
                <HMUserSection @click="logout"
                  :title="$t('GENERAL.LABEL.LOGGED_IN_AS')"
                  :subtitle="user.user_full_name"
                  :actionText="$t('GENERAL.LABEL.LOG_OUT_CHOOSE_DIFFERENT_ACCOUNT')" />
              </div>
              <div v-else
                class="row">
                <div class="col-12 mt-2">
                  <HMTextInput label="Email address:"
                    type="text"
                    rules="required|max:128"
                    name="First name"
                    v-model="accountEmail" />
                </div>
                <div class="col-12 col-md-6">
                  <HMTextInput class="mb-3"
                    label="First name:"
                    type="text"
                    rules="required|max:128"
                    name="First name"
                    v-model="accountFirstName" />
                </div>
                <div class="col-12 col-md-6">
                  <HMTextInput class="mb-3"
                    label="Last name:"
                    type="text"
                    rules="required|max:128"
                    name="Last name"
                    v-model="accountLastName" />
                </div>
              </div>
              <div class="col-12 mt-1">
                <div class="marketing-optin">
                  <HMCheckBox v-if="isSmtpConnected"
                    v-model="optedIn">
                    {{ $t('STUDIO.LABELS.OPT_INTO_MARKETING_EMAILS') }}
                  </HMCheckBox>
                </div>
              </div>
              <div class="col-12 d-flex" v-if="!isLoggedIn">
                <HMCheckBox class="w-auto mt-3"
                  v-model="agreedToTerms">
                  <span>
                    {{ $t('STUDIO_SIGNUP.LABEL.I_AGREE_TO_THE') }}
                    <a class="c-charcoal"
                      target="_blank"
                      @click.stop
                      :href="config.termsOfUseURL">
                      {{ $t('STUDIO_SIGNUP.LINK.TERMS_OF_SERVICE') }}.
                    </a>
                  </span>
                </HMCheckBox>
              </div>
              <div class="col-12 mt-3">
                <label class="mt-3"
                  for="card-element">
                  {{ $t('MARKETING.LABELS.CREDIT_CARD') }}
                </label>
                <div id="card-element"
                  ref="card-element"
                  :class="{ 'is-invalid': stripeError }"></div>
                <div class="invalid-feedback"
                  v-if="stripeError">
                  {{ stripeError }}
                </div>
                <div class="col-12 mt-3">
                  <i class="me-1 text-primary c-brand-color far fa-lock-alt"></i>
                  {{ $t('SIGNUP.TEXT.THIS_IS_A_SECURE_256_BIT') }}
                  <span class="c-charcoal">{{ $t('SIGNUP.TEXT.100_SECURE') }}</span>
                </div>
                <div class="checkout-button-container col-12 mt-4">
                  <HMButton class="w-100"
                    color="brand"
                    :disabled="stripeError || isBooking || !stripeCompleted || !agreedToTerms"
                    @click="book"
                    :loading="isBooking">
                    <span class="w-100 text-center">
                      {{ $t('MARKETING.BUTTONS.PAY_NOW') }}
                    </span>
                  </HMButton>
                </div>
                <div class="col-12 mt-4">
                  <div class="d-flex flex-row justify-content-between align-items-center flex-wrap">
                    <div class="credit-cards d-flex flex-row justify-content-start flex-nowrap mb-2">
                      <img class="img-cc me-2"
                        src="/assets/images/Visa.png" />
                      <img class="img-cc me-2"
                        src="/assets/images/Amex.png" />
                      <img class="img-cc me-2"
                        src="/assets/images/Mastercard.png" />
                      <img class="img-cc mr-2"
                        src="/assets/images/cartes-bancaires.png" />
                    </div>
                    <div class="d-flex">
                      <span>{{ $t('PRODUCTS.LABELS.CHECKOUT_BY') }}</span>
                      <img v-if="isStripeProcessor"
                        class="img-stripe ms-2"
                        src="/assets/images/Stripe.png" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="checkout-summary-desktop d-none d-lg-block offset-lg-1 col-md-4 col-lg-4 bg-lightest-gray full-height">
            <div class="position-fixed ms-2 ms-xl-4 me-2 ms-xl-4 pe-4 pt-4 h-100">
              <div class="d-flex flex-column justify-content-between align-items-start h-100">
                <div class="top-section">
                  <h2>{{ $t('MARKETING.LABELS.ORDER_SUMMARY') }}</h2>
                  <MeetingCheckoutSummary v-if="meetingType"
                    key="desktop-summary"
                    class="mt-3"
                    :meetingType="meetingType"
                    :currency="currency"
                    :instructorNames="instructorNames"
                    :selectedSlot="selectedSlot"
                    :timezone="timezone" />
                </div>
                <div class="bottom-section w-100">
                  <div class="d-flex flex-row justify-content-between align-items-center flex-wrap w-100">
                    <h3 class="me-2">{{ $t('PRODUCTS.LABELS.NEED_ANY_HELP') }}</h3>
                    <a class="undecorated-router-link d-block"
                      :href="`mailto:${studio.email}`">
                      <span>
                        <i class="fal fa-comments-alt c-brand-color"></i>
                        {{ $t('STUDIO.LABELS.CONTACT_US') }}
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else
        class="text-center mt-4">
        <h1>{{ $t('MARKETING.TEXT.THIS_MEETING_TYPE_DOES_NOT_EXIST') }}</h1>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { useStudioStore, useMarketingStore } from '@/stores';
import { errorToMessage } from '@/utils/errors';
import PublicSchedulingCalendar from '@/components/products/PublicSchedulingCalendar.vue';
import { Form } from 'vee-validate';
import { metaTitles } from '@/utils/meta-utils';
import StudioLogo from '@/components/navigation/StudioLogo.vue';
import { getConfig } from '@/utils/configuration';
import MeetingCheckoutSummary from '@/components/marketing/MeetingCheckoutSummary.vue';
import { email } from '@vee-validate/rules';

export default {
  name: 'StudioMeetingBooking',
  components: {
    PublicSchedulingCalendar,
    Form,
    StudioLogo,
    MeetingCheckoutSummary
  },
  setup() {
    definePageMeta({
      name: 'studio-book-meeting',
      isStudio: true,
      hideMobileNav: true,
      hideTopBar: true,
      hideInstagramSlider: true,
      hideStudioContactInformation: true,
      hideCookieConsent: true,
      hideStudioAdminFooter: true,
      preventRefreshModal: true,
    })
    const url = useRequestURL();

    return {
      config: getConfig(url),
    };
  },
  metaInfo() {
    return {
      title: this.metaTitle,
      meta: [
        ...metaTitles(this.metaTitle),
        {
          vmid: 'og:image',
          property: 'og:image',
          content:
            this.studio?.cover_photo?.storage_url ??
            this.studio?.cover_photo?.embedded_image_url,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: getCurrentUrl(),
        },
      ],
    };
  },
  computed: {
    ...mapState(useStudioStore, {
      studio: 'currentStudio',
      stripePublishableKey: 'stripePublishableKey',
      isStripeProcessor: 'isStripeProcessor',
      currency: 'currency',
    }),
    ...mapState(useUserStore, {
      isLoggedIn: 'isLoggedIn',
      user: 'user',
    }),
    isSmtpConnected() {
      return this.studio?.is_smtp_connected;
    },
    metaTitle() {
      if (!this.meetingType) return '';
      if (!this.meetingType.instructors.length) return this.meetingType.name;

      return `${this.meetingType.name} ${this.$t('MARKETING.LABELS.WITH')} ${this.instructorNames
        }`;
    },
    instructorNames() {
      return this.meetingType?.instructors.map(i => i.name).join(', ');
    },
    studioURL() {
      return this.$studioURL;
    },
    questions() {
      if (!this.meetingType?.form || !this.meetingType.form.questions)
        return [];
      return (
        [...this.meetingType.form.questions].sort(
          (p1, p2) => p1.position - p2.position
        ) ?? []
      );
    },
    shouldShowPayment() {
      return this.meetingType?.require_payment && this.meetingType?.price;
    },
  },
  data() {
    return {
      isLoading: true,
      meetingType: null,
      currentStep: 'calendar',
      selectedSlot: null,
      timezone: null,
      inProgress: false,
      bookingFullName: null,
      bookingEmail: null,
      accountFirstName: null,
      accountLastName: null,
      accountEmail: null,
      isBooking: false,
      stripeError: false,
      stripeCompleted: false,
      stripe: null,
      card: null,
      optedIn: false,
      agreedToTerms: false,
    };
  },
  methods: {
    logout() {
      useUserStore().logout();
    },
    async loadData() {
      this.isLoading = true;
      try {
        this.meetingType = await useMarketingStore().getPublicMeetingType({
          studioURL: this.studioURL,
          slug: this.$route.params.meeting_type_slug,
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Could not load this meeting type',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    selectSlot({ selectedSlot, selectedTimezone }) {
      this.selectedSlot = selectedSlot;
      this.timezone = selectedTimezone;
      this.currentStep = 'questions';
    },
    async book() {
      console.log('book shouldShowPayment', this.shouldShowPayment);
      console.log('book currentStep', this.currentStep);
      if (this.shouldShowPayment && this.currentStep !== 'payment') {
        this.currentStep = 'payment';
        return;
      }

      this.isBooking = true;

      const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;

      let paymentIntentId = null;
      let paymentMethodResponse: any = {};

      if (this.shouldShowPayment) {
        // get payment intent

        try {
          const reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
            action: 'meetingintent',
          });

          const { client_secret, payment_intent_id } = await useMarketingStore().meetingPaymentIntent({
            studioURL: this.studioURL,
            slug: this.meetingType.slug,
            slot: this.selectedSlot,
            timezone: this.timezone,
            firstName: this.accountFirstName,
            lastName: this.accountLastName,
            email: this.accountEmail,
            token: reCaptchaToken,
          });

          paymentIntentId = payment_intent_id
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to book your slot!',
            text: errorToMessage(error.response),
          });
          console.error('Error booking the meeting:', error)
          return
        }
                
        const { error, paymentMethod } = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: {
            email: this.accountEmail,
            name: `${this.accountFirstName} ${this.accountLastName}`
          }
        })
        paymentMethodResponse = paymentMethod
        if (error) {
          this.isBooking = false;          
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to create a payment method!',
            text: errorToMessage(error.response),
          });
          return
        }
      }

      const reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
        action: 'meetingbooking',
      });

      try {
        const bookedMeeting = await useMarketingStore().bookPublicMeetingSlot({
          studioURL: this.studioURL,
          slug: this.meetingType.slug,
          slot: this.selectedSlot,
          timezone: this.timezone,
          bookingFullName: this.bookingFullName,
          bookingEmail: this.bookingEmail,
          userEmail: this.accountEmail,
          userFirstName: this.accountFirstName,
          userLastName: this.accountLastName,
          form: this.questions,
          token: reCaptchaToken,
          paymentIntentId,
          paymentMethodId: paymentMethodResponse.id,
          marketingOptIn: this.optedIn,
        });
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'PRODUCTS.NOTIFICATIONS.PRIVATE_SESSION_SUCCESSFULLY_BOOKED_ON',
            {
              slot: this.$formatDate(
                this.selectedSlot.toDate(),
                'date-time-long'
              ),
            }
          ),
        });
        this.$router.push({
          name: 'studio-booked-meeting',
          params: {
            studio_slug: this.studioURL,
            meeting_unique_id: bookedMeeting.unique_id,
          },
        });
      } catch (error) {
        console.log(error);
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to book your slot!',
          text: errorToMessage(error.response),
        });
      } finally {
        this.isBooking = false;
      }
    },
    stripeChangeCallback(event) {
      if (event.complete) {
        this.stripeCompleted = true;
        this.stripeError = false;
      } else if (event.error) {
        this.stripeCompleted = false;
        this.stripeError = event.error.message;
      }
    },
    initStripe() {
      this.stripe = window.Stripe(
        this.stripePublishableKey
      );
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        style,
        hidePostalCode: true,
      });
      this.card.addEventListener('change', this.stripeChangeCallback);
      const mountTimer = setInterval(
        () => {
          console.log('Waiting for stripe card element');
          if (this.$refs['card-element']) {
            this.card.mount(this.$refs['card-element']);
            console.log('Stripe card element mounted');
            clearInterval(mountTimer);
          }
        },
        50,
        100
      );
    },

  },
  async mounted() {
    await this.loadData();
    this.initStripe();
  },
  watch: {
    bookingFullName(value) {
      if (!value) return;
      const [firstName, lastName] = value.split(' ');
      this.accountFirstName = firstName;
      this.accountLastName = lastName;
    },
    bookingEmail(value) {
      if (!value) return;
      this.accountEmail = value;
    },
    user: {
      handler(value) {
        if (!value) return;
        this.bookingEmail = value.user_email;
        this.bookingFullName = value.user_full_name;
      },
      immediate: true,
    },
    isLoggedIn: {
      handler(value) {
        if (!value) return;
        this.agreedToTerms = true;
      },
      immediate: true,
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';

.meeting-booking-container {}

/* Prev Scrolling */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Back Scrolling */
.slide-fade-reverse-enter-active,
.slide-fade-reverse-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-reverse-enter {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-fade-reverse-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.transition-card+.transition-card {
  position: absolute;
}

.transition-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.img-stripe {
  height: 22px;
  width: auto;
}

.img-cc {
  height: 40px;
  width: auto;

  @include media-breakpoint-down(xs) {
    height: 30px;
    width: auto;
  }
}

.full-height {
  min-height: 100vh;
}

.logo-image {
  display: block;
  height: 60px;
  width: auto;
}

.back-button {
  display: flex;
  align-items: center;
  padding: 0 16px;
  margin-left: -10px;
}


.bottom-section {
  padding-bottom: 4rem;
}

.checkout-button-container {
  @include media-breakpoint-only(xs) {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: white;
    height: 70px;
    width: 100%;
    z-index: 1031;
    padding: 1rem 15px;
    box-shadow: 0px -1px 5px rgba(50, 50, 50, 0.2);
  }
}
</style>
